// Keep 'use client' at the top
'use client'

import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'
import Image from 'next/image'
import { useLanguage } from '../../providers/LanguageProvider'
import ScrollContainer from 'react-indiana-drag-scroll'
import clsx from 'clsx'
import Link from 'next/link'
import { Container } from 'lucide-react'

interface Service {
  homeicon: {
    thumbnailURL?: string
    [key: string]: any
  }
  image: {
    thumbnailURL?: string
    [key: string]: any
  }
  title: string
  content?: any
  ref_name?: any
}

const ServicesSection = ({ services }: { services: Service[] }) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.why-heading', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          once: true,
        },
      })

      gsap.from('.why-paragraph', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        delay: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          once: true,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="w-full relative overflow-x-hidden bg-white pt-36 pb-6 sm:pb-10 sm:pt-28 lg:pt-52 lg:pb-[180px] 2xl:py-36"
    >
      <img
        src="/images/vehicles.svg"
        alt="side image"
        className={clsx(
          // Size
          'absolute size-36 sm:size-44 md:size-44 xl:size-[270px] lg:top-4 xl:top-20 xl:size-70 top-0 2xl:top-20',
          // Flip and position based on language
          isArabic ? 'right-0  scale-x-[-1]' : 'left-0',
        )}
      />
      <div className="section-container mx-auto flex flex-col sm:items-center 2xl:gap-5 justify-center">
        <h1 className=" text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] why-heading font-semibold text-[#414141] mb-6 lg:mb-8">
          {isArabic ? 'خدماتنا' : 'Our Services'}
        </h1>
        <p className="text-[15px] sm:text-[15px] md:text-[16px] 2xl:text-lg xl:w-[65%] leading-5 why-paragraph text-gray-600 sm:text-center">
          {isArabic
            ? 'من مبيعات الحاويات إلى الدعم اللوجستي الكامل، نحن هنا لدعم عملياتك.'
            : 'From container sales, leasing, and modifications to cargo storage, maintenance, and full logistics support-our end-to-end solutions are designed to keep your operations moving securely and efficiently.'}
        </p>

        <div className="w-screen focus:outline-none scrollbar-hide text-black mt-16 pb-14">
          <ScrollContainer
            nativeMobileScroll={true}
            hideScrollbars={false}
            className="flex gap-4 sm:gap-5 focus:outline-none md:gap-8 overflow-x-scroll scrollbar-none  px-4 mr-6 sm:mr-0 sm:px-6 md:px-10 xl:px-10 2xl:px-60 snap-x snap-mandatory sm:snap-none scroll-smooth"
          >
            {services.map((service, idx) => {
              const imageUrl = service.image?.thumbnailURL
              const iconUrl = service.homeicon?.thumbnailURL

              return (
                <div
                  key={idx}
                  className="snap-start w-[97%] sm:w-[95%] md:w-[340px] xl:w-[390px] shrink-0 bg-white flex flex-col"
                >
                  {/* Image */}
                  {/* Image with Link */}
                  <Link
                    href={
                      isArabic
                        ? `${process.env.NEXT_PUBLIC_SERVER_URL}/${service.ref_name}`
                        : `/services/${service.ref_name}`
                    }
                    className="relative w-full h-[360px] sm:h-[400px] md:h-[450px] lg:h-[490px] 2xl:h-[550px] mb-4 overflow-hidden bg-gray-200 block"
                  >
                    {imageUrl ? (
<Image
  src={imageUrl}
  alt={service.title}
  fill
  sizes="(max-width: 768px) 97vw, (max-width: 1280px) 340px, 390px"
  className="object-cover transition-transform duration-300 hover:scale-105"
/>
                    ) : (
                      <Image
                        src={'/images/placeholder.png'}
                        alt={service.title}
                        fill
                        className="object-cover transition-transform duration-300 hover:scale-105"
                      />
                    )}
                  </Link>

                  <div className="flex gap-[6px]  mb-3">
                    {/* Icon */}
                    {iconUrl ? (
                      <img
                        className="mt-[2px] size-4 w-5 h-5 md:w-6 md:h-6"
                        src={iconUrl}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          ;(e.currentTarget.nextElementSibling as HTMLElement)!.style.display =
                            'inline-block'
                        }}
                      />
                    ) : null}

                    {/* Lucide fallback icon */}
                    {!iconUrl && (
                      <Container className="mt-[2px] size-4 md:w-6 md:h-6 whitespace-nowrap text-gray-400" />
                    )}
                    {/* Title */}
                    <p className="text-[18px] sm:text-[17px] md:text-[18px] xl:text-[21px] tracking-tight font-normal line-clamp-1">
                      {service.title}
                    </p>
                  </div>

                  {/* Excerpt */}
                  <p className="text-[15px] sm:text-[13.5px] md:text-[16px] sm:h-[40px] md:h-[60px] text-[#666666] font-normal mb-6 leading-5 line-clamp-3">
                    {service.content?.root?.children?.[0]?.children?.[0]?.text}
                  </p>

                  {/* CTA */}
                  {isArabic ? (
                    <Link
                      href={`${process.env.NEXT_PUBLIC_SERVER_URL}/${service.ref_name}`}
                      className="text-red-600 text-[15px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-normal hover:underline mt-auto"
                    >
                      اعرف المزيد
                    </Link>
                  ) : (
                    <Link
                      href={`services/${service.ref_name}`}
                      className="text-red-600 text-[15px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-normal hover:underline mt-auto"
                    >
                      Know More
                    </Link>
                  )}
                </div>
              )
            })}
          </ScrollContainer>
        </div>
      </div>
    </section>
  )
}

export default ServicesSection
